import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiRefreshCw, FiShield } from 'react-icons/fi';
import { api } from '../../services/api';
import { formatLocalDate } from '../../utils/formatters';
import { getScopeLabel } from './scopeLabels';

function getStatusTone(active) {
  return active
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
    : 'border-slate-500/20 bg-slate-500/10 text-slate-300';
}

export function TradingAuthorityPanel({ active }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    if (!active) return;
    loadStatus();
  }, [active]);

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.getSkillsTradingAuthorizationStatus();
      if (res.code !== 'SUCCESS') {
        throw new Error(res.msg || t('account.tradingAuthority.loadFailed'));
      }
      setStatus(res.data || null);
    } catch (err) {
      setError(err.message || t('account.tradingAuthority.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return t('account.tradingAuthority.persistent');
    return formatLocalDate(
      value,
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      },
      i18n.language
    );
  };

  const handleRevoke = async () => {
    const grantId = status?.grant?.grant_id || null;
    if (!grantId) return;
    if (!window.confirm(t('account.tradingAuthority.revokeConfirm'))) {
      return;
    }

    setRevoking(true);
    setError('');
    setNotice('');
    try {
      const res = await api.revokeSkillsTradingAuthorization(grantId);
      if (res.code !== 'SUCCESS') {
        throw new Error(res.msg || t('account.tradingAuthority.revokeFailed'));
      }
      setNotice(t('account.tradingAuthority.revokeSuccess'));
      await loadStatus();
    } catch (err) {
      setError(err.message || t('account.tradingAuthority.revokeFailed'));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-surface-highlight/60 via-surface/90 to-surface-highlight/30 rounded-3xl p-6 border border-white/10 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-emerald-500/10 border border-emerald-400/20 flex items-center justify-center">
            <FiShield className="text-emerald-300" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
              {t('account.tradingAuthority.eyebrow')}
            </div>
            <h2 className="mt-1 text-lg font-semibold text-white">
              {t('account.tradingAuthority.title')}
            </h2>
            <p className="text-sm text-slate-400 mt-2">
              {t('account.tradingAuthority.description')}
            </p>
          </div>
        </div>
        <button
          onClick={loadStatus}
          className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
        >
          {t('account.apiKeys.refresh')}
        </button>
      </div>

      {notice && (
        <div className="mb-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
          {notice}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-8 text-sm text-slate-400">{t('common.loading')}</div>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                {t('account.tradingAuthority.walletLabel')}
              </div>
              <div className="mt-2 break-all font-mono text-sm text-white">
                {status?.wallet_address || t('account.tradingAuthority.noWallet')}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                {t('account.tradingAuthority.walletAuthorization')}
              </div>
              <div className="mt-2">
                <span className={`rounded-full border px-2 py-1 text-[11px] uppercase tracking-[0.16em] ${getStatusTone(Boolean(status?.wallet_authorized))}`}>
                  {status?.wallet_authorized
                    ? t('account.tradingAuthority.authorized')
                    : t('account.tradingAuthority.notAuthorized')}
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                {t('account.tradingAuthority.agentGrant')}
              </div>
              <div className="mt-2">
                <span className={`rounded-full border px-2 py-1 text-[11px] uppercase tracking-[0.16em] ${getStatusTone(Boolean(status?.grant_active))}`}>
                  {status?.grant_active
                    ? t('account.tradingAuthority.active')
                    : t('account.tradingAuthority.inactive')}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
            <div className="flex items-start gap-2">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <div>{t('account.tradingAuthority.defaultHint')}</div>
            </div>
          </div>

          {status?.grant_active && status?.grant ? (
            <div className="mt-5 rounded-[24px] border border-white/10 bg-black/10 p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="text-sm font-semibold text-white">
                    {t('account.tradingAuthority.currentGrant')}
                  </div>
                  <p className="mt-1 text-sm text-slate-400">
                    {t('account.tradingAuthority.currentGrantDesc')}
                  </p>
                </div>
                <button
                  onClick={handleRevoke}
                  disabled={revoking}
                  className="rounded-xl border border-rose-500/20 px-4 py-2 text-sm text-rose-200 hover:bg-rose-500/10 disabled:opacity-50 transition-colors"
                >
                  {revoking ? t('common.processing') : t('account.tradingAuthority.revokeAction')}
                </button>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                    {t('account.tradingAuthority.modeLabel')}
                  </div>
                  <div className="mt-2 text-sm text-white">
                    {t(`account.tradingAuthority.modes.${status.grant.authorization_mode || 'persistent'}`)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                    {t('account.tradingAuthority.expiresLabel')}
                  </div>
                  <div className="mt-2 text-sm text-white">
                    {formatDate(status.grant.expires_at)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                    {t('account.tradingAuthority.grantIdLabel')}
                  </div>
                  <div className="mt-2 break-all font-mono text-xs text-slate-300">
                    {status.grant.grant_id}
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                  {t('account.tradingAuthority.scopeLabel')}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(status.grant.approved_scopes || []).map((scope) => (
                    <span
                      key={scope}
                      className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[11px] text-emerald-200"
                    >
                      {getScopeLabel(t, scope)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5 rounded-[24px] border border-dashed border-white/10 bg-white/5 px-4 py-6 text-sm text-slate-400">
              {status?.wallet_authorized
                ? t('account.tradingAuthority.noGrant')
                : t('account.tradingAuthority.noWalletAuthorization')}
            </div>
          )}
        </>
      )}
    </div>
  );
}
